import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "@emotion/styled";
import Spinner from "@components/Spinner";
import { useLang } from "@src/hooks/useLang";
import {
  reportsService,
  type FullReport,
} from "@services/reportsService";
import ReportMaps from "./ReportMaps";

// ── Styled ──────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  background: #f1f2f4;
  padding: 32px 16px 64px;
  font-family: Nunito Sans, sans-serif;
  color: #2b2f38;
`;

const Sheet = styled.div`
  max-width: 820px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  padding: 36px 40px 44px;
  @media (max-width: 640px) {
    padding: 24px 18px 32px;
  }
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 800;
  color: #2d5016;
  margin: 0 0 4px;
`;

const Sub = styled.div`
  font-size: 13px;
  color: #899093;
  font-weight: 600;
`;

const DownloadBtn = styled.a`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #68ad18;
  color: #fff;
  text-decoration: none;
  font-weight: 700;
  font-size: 13px;
  padding: 9px 16px;
  border-radius: 8px;
  transition: 0.2s;
  &:hover {
    background: #5c9a15;
  }
`;

const Banner = styled.div`
  background: #f1f6ec;
  border-left: 4px solid #68ad18;
  border-radius: 6px;
  padding: 14px 16px;
  font-size: 15px;
  font-weight: 700;
  color: #2d5016;
  margin-bottom: 24px;
  line-height: 1.4;
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Metric = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px 14px;
`;

const MetricLabel = styled.div`
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #899093;
  font-weight: 700;
  margin-bottom: 6px;
`;

const MetricValue = styled.div<{ color?: string }>`
  font-size: 22px;
  font-weight: 800;
  color: ${({ color }) => color ?? "#2b2f38"};
  text-transform: capitalize;
`;

const Note = styled.div`
  font-size: 13px;
  color: #6b7178;
  margin-bottom: 28px;
  line-height: 1.5;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 800;
  color: #2d5016;
  margin: 28px 0 12px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  th,
  td {
    text-align: left;
    padding: 9px 12px;
  }
  thead {
    background: #4a7c2e;
    color: #fff;
    font-size: 12px;
  }
  tbody tr:nth-of-type(even) {
    background: #f5f5f5;
  }
`;

const Gap = styled.div`
  margin-top: 10px;
  font-weight: 800;
  color: #2d5016;
  font-size: 14px;
`;

const Para = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: #3a3f48;
  margin: 0 0 12px;
`;

const List = styled.ul`
  margin: 0;
  padding-left: 20px;
  li {
    font-size: 14px;
    line-height: 1.55;
    margin-bottom: 8px;
    color: #3a3f48;
  }
`;

const Caveat = styled.li`
  color: #8a8f96 !important;
`;

const SpotList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
`;

const Spot = styled.a<{ kind: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  font-size: 13px;
  color: #3a3f48;
  padding: 7px 10px;
  border-radius: 7px;
  background: #f8f9fa;
  transition: 0.15s;
  &:hover {
    background: #eef0f2;
  }
  &::before {
    content: "";
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({ kind }) => (kind === "underperform" ? "#e23b3b" : "#2f6fe2")};
  }
`;

const SpotMaps = styled.span`
  margin-left: auto;
  color: #4a7c2e;
  font-weight: 700;
  white-space: nowrap;
`;

const Footer = styled.div`
  margin-top: 36px;
  padding-top: 16px;
  border-top: 1px solid #eceef0;
  font-size: 11px;
  color: #aeb3b9;
  text-align: center;
`;

const Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
`;

// ── Helpers ─────────────────────────────────────────────────

const STRENGTH_COLOR: Record<string, string> = {
  "very strong": "#006837",
  strong: "#3f9142",
  moderate: "#b58a00",
  weak: "#d9682f",
  none: "#a50026",
};

const fmt = (v: unknown, dp = 3): string =>
  typeof v === "number" ? v.toFixed(dp) : "—";

const fmtP = (v: unknown): string =>
  typeof v !== "number" ? "—" : v < 0.001 ? "< 0.001" : v.toFixed(3);

const tpl = (s: string, vars: Record<string, string | number>): string =>
  s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`));

// ── Component ───────────────────────────────────────────────

const ReportView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const lang = useLang();
  const t = (lang as any).reports ?? {};
  const [report, setReport] = useState<FullReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    reportsService
      .getReport(id)
      .then((r) => !cancelled && setReport(r))
      .catch(() => !cancelled && setError(t.notFound ?? "Report not found."));
    return () => {
      cancelled = true;
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (report) document.title = report.title;
  }, [report]);

  if (error) return <Center>{error}</Center>;
  if (!report)
    return (
      <Center>
        <Spinner />
      </Center>
    );

  const s = report.stats;
  const predictor = s.predictorLabel ?? "Productivity";
  const outcome = s.outcomeLabel ?? "Yield";
  const outcomeUnit = s.outcomeUnit ?? "";
  const strengthColor = STRENGTH_COLOR[s.strength] ?? "#2b2f38";
  const under = s.disagreements.filter((d) => d.kind === "underperform").length;
  const over = s.disagreements.filter((d) => d.kind === "overperform").length;

  const createdStr = (() => {
    try {
      return new Date(report.createdAt).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return report.createdAt;
    }
  })();

  return (
    <Page>
      <Sheet>
        <TopRow>
          <div>
            <Title>{report.title}</Title>
            <Sub>
              {createdStr}
              {s.yearsUsed && s.yearsUsed.length > 0
                ? ` · ${predictor} from ${s.yearsUsed[0]}–${
                    s.yearsUsed[s.yearsUsed.length - 1]
                  }`
                : ""}
            </Sub>
          </div>
          <DownloadBtn href={reportsService.reportPdfUrl(report.id)} target="_blank" rel="noreferrer">
            ⬇ {t.download ?? "Download PDF"}
          </DownloadBtn>
        </TopRow>

        {report.narrative?.headline && <Banner>{report.narrative.headline}</Banner>}

        <MetricGrid>
          <Metric>
            <MetricLabel>{t.pearson ?? "Pearson r"}</MetricLabel>
            <MetricValue color={strengthColor}>{fmt(s.pearson)}</MetricValue>
          </Metric>
          <Metric>
            <MetricLabel>{t.spearman ?? "Spearman ρ"}</MetricLabel>
            <MetricValue>{fmt(s.spearman)}</MetricValue>
          </Metric>
          <Metric>
            <MetricLabel>{t.strength ?? "Strength"}</MetricLabel>
            <MetricValue color={strengthColor}>{s.strength}</MetricValue>
          </Metric>
          <Metric>
            <MetricLabel>{t.cells ?? "Cells"}</MetricLabel>
            <MetricValue>{s.n}</MetricValue>
          </Metric>
        </MetricGrid>

        <Note>
          {`R² = ${fmt(s.r2)} · ${
            s.significant
              ? `${t.significant ?? "Statistically significant"} (p = ${fmtP(s.pValue)})`
              : `${t.notSignificant ?? "Not statistically significant"} (p = ${fmtP(s.pValue)})`
          }`}
          {s.nonlinear && (
            <>
              <br />
              {t.nonlinear ??
                "Relationship is nonlinear (Spearman exceeds Pearson) — likely a saturating signal."}
            </>
          )}
        </Note>

        <ReportMaps
          fieldId={report.fieldId}
          seasonId={report.seasonId}
          predictorLayer={report.params?.layerA ?? "productivity"}
          predictorLabel={predictor}
          outcomeLabel={outcome}
          ndviDate={(report.params as any)?.ndviDate ?? null}
          disagreements={s.disagreements}
        />

        {s.zones.length > 0 && (
          <>
            <SectionTitle>
              {tpl(t.zoneTableTitle ?? "{outcome} by {predictor} zone", {
                outcome,
                predictor,
              })}
            </SectionTitle>
            <Table>
              <thead>
                <tr>
                  <th>{t.zoneCol ?? "Zone"}</th>
                  <th>{t.zoneCells ?? "Cells"}</th>
                  <th>
                    {t.zoneMean ?? "Mean"} {outcomeUnit}
                  </th>
                  <th>{t.zoneStdDev ?? "Std dev"}</th>
                </tr>
              </thead>
              <tbody>
                {s.zones.map((z) => (
                  <tr key={z.zone}>
                    <td>{z.label}</td>
                    <td>{z.n}</td>
                    <td>{fmt(z.meanB, 2)}</td>
                    <td>{fmt(z.sdB, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {s.zoneGap != null && (
              <Gap>
                {t.zoneGap ?? "Gap (highest − lowest zone)"}: {fmt(s.zoneGap, 2)} {outcomeUnit}
              </Gap>
            )}
          </>
        )}

        {s.disagreements.length > 0 && (
          <>
            <SectionTitle>{t.investigateTitle ?? "Areas to investigate"}</SectionTitle>
            <Para>
              {under > 0 &&
                tpl(
                  t.underperform ??
                    "{n} area(s) are HIGH {predictor} but LOW {outcome} (underperforming — check drainage, disease, compaction, planter skips).",
                  { n: under, predictor: predictor.toLowerCase(), outcome: outcome.toLowerCase() }
                )}
              {over > 0 && (
                <>
                  {" "}
                  {tpl(
                    t.overperform ??
                      "{n} area(s) are LOW {predictor} but HIGH {outcome} (overperforming).",
                    { n: over, predictor: predictor.toLowerCase(), outcome: outcome.toLowerCase() }
                  )}
                </>
              )}
            </Para>
            <SpotList>
              {s.disagreements
                .filter((d) => typeof d.lat === "number" && typeof d.lng === "number")
                .slice(0, 12)
                .map((d, i) => (
                  <Spot
                    key={i}
                    kind={d.kind}
                    href={`https://www.google.com/maps?q=${d.lat},${d.lng}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {d.kind === "underperform"
                      ? t.underperformShort ?? "Underperforming spot"
                      : t.overperformShort ?? "Overperforming spot"}
                    <span style={{ color: "#899093" }}>
                      {d.lat!.toFixed(5)}, {d.lng!.toFixed(5)}
                    </span>
                    <SpotMaps>↗ Google Maps</SpotMaps>
                  </Spot>
                ))}
            </SpotList>
          </>
        )}

        {report.narrative ? (
          <>
            {report.narrative.interpretation && (
              <>
                <SectionTitle>{t.interpretation ?? "Interpretation"}</SectionTitle>
                <Para>{report.narrative.interpretation}</Para>
              </>
            )}
            {report.narrative.recommendations?.length > 0 && (
              <>
                <SectionTitle>{t.recommendations ?? "Recommendations"}</SectionTitle>
                <List>
                  {report.narrative.recommendations.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </List>
              </>
            )}
            {report.narrative.caveats?.length > 0 && (
              <>
                <SectionTitle>{t.caveats ?? "Caveats"}</SectionTitle>
                <List>
                  {report.narrative.caveats.map((c, i) => (
                    <Caveat key={i}>{c}</Caveat>
                  ))}
                </List>
              </>
            )}
          </>
        ) : (
          <Note style={{ fontStyle: "italic", marginTop: 24 }}>
            {t.noNarrative ?? "AI interpretation was unavailable; the statistics are complete."}
          </Note>
        )}

        <Footer>
          {t.footer ??
            "Correlation is not causation — treat findings as leads to verify in-field."}
        </Footer>
      </Sheet>
    </Page>
  );
};

export default ReportView;
